import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Edit } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminPolicies = () => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => {
    axios.get(`${API}/policies`)
      .then(r => setPolicies(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openEdit = (policy) => {
    setEditing({ ...policy });
    setOpen(true);
  };

  const save = async () => {
    try {
      await axios.put(`${API}/policies/${editing.id}`, {
        title: editing.title,
        description: editing.description,
        content: editing.content
      });
      toast.success('Policy section updated');
      setOpen(false);
      load();
    } catch {
      toast.error('Failed to save changes');
    }
  };

  return (
    <AdminLayout title="Manage Policies">
      <p className="text-gray-500 mb-6 text-sm">
        Edit each policy section below. Changes appear immediately on the public Policies page.
      </p>

      {loading ? <div className="text-center py-8 text-gray-500">Loading...</div> : (
        <div className="space-y-4">
          {policies.map((policy) => (
            <Card key={policy.id} className="border-2">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{policy.title}</CardTitle>
                    <p className="text-sm text-gray-500 mt-1">{policy.description}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEdit(policy)}
                    data-testid={`edit-policy-${policy.id}`}
                  >
                    <Edit size={15} className="mr-1" /> Edit
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-700 whitespace-pre-wrap max-h-40 overflow-y-auto">
                  {policy.content}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit: {editing?.title}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label>Section Title</Label>
                <Input
                  value={editing.title}
                  onChange={e => setEditing({ ...editing, title: e.target.value })}
                />
              </div>
              <div>
                <Label>Subtitle / Description</Label>
                <Input
                  value={editing.description}
                  onChange={e => setEditing({ ...editing, description: e.target.value })}
                />
              </div>
              <div>
                <Label>Content</Label>
                <p className="text-xs text-gray-400 mb-1">
                  Use a blank line between paragraphs. Start a line with the heading text followed by a colon, then put the details on the next line.
                </p>
                <Textarea
                  rows={14}
                  value={editing.content}
                  onChange={e => setEditing({ ...editing, content: e.target.value })}
                  className="font-mono text-sm"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} className="bg-red-600 hover:bg-red-700 text-white">
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};
